/**
 * COVERAGE MOMENTUM — how a Role's automation coverage has MOVED, against the
 * snapshot series behind it.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE TWIN OF `RoleRunMomentum`, AND THE ARITHMETIC IS DELIBERATELY DIFFERENT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `RoleRunMomentum` (`org-roles-run-health-view.schema.ts`) compares two RUN
 * COUNTS, so it must DIVIDE, and division is what forces its floor
 * (`ROLE_RUN_MOMENTUM_MIN_PRIOR_RUNS`) and its `no-prior-runs` arm: a prior of
 * zero has no reciprocal, and the house's own `PercentageChange.calculate`
 * answers `100` there — a fabricated doubling that is more believable than
 * `∞` and therefore worse.
 *
 * 🚨 COVERAGE IS ALREADY A RATIO, SO THE HONEST DELTA IS A SUBTRACTION. The
 * change from 12% to 18% is `+6 PERCENTAGE POINTS`, and computing it needs no
 * denominator at all. That single difference deletes the entire zero-prior
 * problem rather than guarding against it: a prior coverage of `0` is a
 * perfectly good baseline for a subtraction, and there is no magnitude below
 * which the arithmetic destabilises. So this schema has NO minimum-prior floor
 * and needs none, and nothing here may ever be expressed as a percentage
 * CHANGE — `0.02 → 0.04` is `+2 points`, never `+100%`.
 *
 * What DOES need a floor here is the SPAN. A subtraction is stable at any
 * magnitude and meaningless at any duration: two points a day apart, labelled
 * as a month's movement, is a wrong number wearing a correct one's clothes.
 * {@link ROLE_COVERAGE_MOMENTUM_MIN_SPAN_DAYS} is that floor, and
 * {@link RoleCoverageMomentumSchema.spanDays} serves the span the delta really
 * covers so a client can never label it with a window the server did not use.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 A PRIOR POINT CANNOT BE RECONSTRUCTED, EVER. THAT IS WHY IT IS STORED.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Run momentum needed no new table: `WorkflowExecution` is a LEDGER, every row
 * carries `createdAt`, and any past window can be re-counted on demand.
 *
 * Coverage has no ledger. It is DERIVED, on every read, from `RoleWorkload`,
 * `RoleSystemImpact` and `OrganizationAutomationSettings` AS THEY ARE NOW —
 * none of which keeps a history. So there is nothing to look back at, a
 * previous coverage figure is not recoverable by any query, and the only way
 * to know what it was is to have written it down at the time. Hence
 * `RoleCoverageSnapshot`, and hence the two states below that say plainly
 * "nobody was watching yet" instead of inventing a baseline.
 *
 * That cost was put to the CEO and accepted: a permanent schema change, a
 * dash for the first {@link ROLE_COVERAGE_MOMENTUM_MIN_SPAN_DAYS} days, and
 * permanent blindness about every day before the capture began.
 */
import { z } from "zod";

/**
 * How far back the trend looks, in days.
 *
 * A month, because that is the period an owner reads a coverage figure over
 * and the period the register's own copy names. It is a CONSTANT and not a
 * query parameter, for the same reason {@link ROLE_RUN_HEALTH_WINDOW_DAYS} is:
 * a window a client picks is a window the server did not use, and every figure
 * below moves when it changes.
 *
 * ⚠️ IT IS NOT THE RETENTION. The snapshot table keeps far more than this so a
 * longer read stays buildable later; this is only how far back the register's
 * own band reaches today. Widening it is a contract change and cannot outrun
 * the retention — see `ROLE_COVERAGE_SNAPSHOT_RETENTION_DAYS`.
 */
export const ROLE_COVERAGE_MOMENTUM_WINDOW_DAYS = 30;

/**
 * The shortest span two snapshots may be apart and still be reported as a
 * movement.
 *
 * 🚨 THIS IS A DURATION FLOOR, NOT A MAGNITUDE ONE, AND THE DISTINCTION IS THE
 * WHOLE REASON THIS SCHEMA DIFFERS FROM ITS RUN-MOMENTUM TWIN. There is no
 * denominator here to destabilise, so no run-count-style floor applies. What
 * does apply is that the band is captioned as a {@link
 * ROLE_COVERAGE_MOMENTUM_WINDOW_DAYS}-day movement: a delta measured over one
 * day and drawn under that caption is out by a factor of thirty, and nothing
 * on the surface would say so.
 *
 * Seven days, because a week is the shortest span over which the caption is
 * wrong by less than a factor of five, and because coverage moves when somebody
 * AUTHORS a model rather than continuously — a sub-week span is far more likely
 * to be measuring the series' own youth than the Role's.
 *
 * A span under this floor still serves BOTH endpoints and the real
 * `spanDays`, because "18%, and 17% four days ago" is checkable and bounded
 * where "+1 point over 30 days" is simply false.
 */
export const ROLE_COVERAGE_MOMENTUM_MIN_SPAN_DAYS = 7;

/**
 * What a coverage delta is allowed to SAY, as a closed vocabulary.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 FIVE OF THESE SIX STATES CANNOT CARRY A NUMBER, AND THE STATE IS DECIDED
 * BEFORE THE SUBTRACTION
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every arm below is a situation in which `current − prior` would render
 * something, and in which what it rendered would be a claim nobody measured.
 * Deciding the state first is what makes an unmeaningful delta UNREPRESENTABLE
 * rather than computed-and-then-suppressed.
 *
 * | state | the situation | why no number |
 * |---|---|---|
 * | `comparable` | two modelled points, at least `MIN_SPAN_DAYS` apart | — it is served |
 * | `not-modelled` | the CURRENT coverage carries no ratio | there is no left-hand operand. A Role with no working-time model has no coverage today, not a coverage of zero |
 * | `prior-not-modelled` | the prior point exists but carried no ratio | a move from "no figure" to 18% is not a rise of 18 points; the Role became MEASURABLE, which is a different event and a better sentence |
 * | `too-few-days` | a prior point exists, closer than `MIN_SPAN_DAYS` | the delta is real and the caption is not. Both endpoints are served instead |
 * | `role-too-new` | no prior point, and the Role was created after the window opened | the empty series is a statement about the Role's age |
 * | `not-yet-captured` | no prior point, and the Role predates the window | the empty series is a statement about US — the snapshot series does not reach back that far |
 *
 * 🚨 `role-too-new` AND `not-yet-captured` ARE THE SAME EMPTY SERIES AND
 * OPPOSITE INSTRUCTIONS TO A READER, WHICH IS THE ONLY REASON BOTH EXIST. One
 * says the Role has no history because it has no past; the other says it has no
 * history because nobody was recording. Collapsing them into one "no data" arm
 * tells an owner of a six-month-old Role that their Role is new.
 *
 * 🚨 AN AGE NEVER VETOES A PRESENT PRIOR POINT. `role-too-new` is consulted
 * ONLY where no prior snapshot was found. The opposite rule — age first — was
 * shipped on the run-momentum twin and staging falsified it the same hour: runs
 * attach to a WORKFLOW and never to a Role, so a Role created days ago that is
 * given an established workflow inherits its whole history at once, and one
 * such Role read 168 runs in EACH window while the age rule withheld a
 * checkable 0% behind "nothing to compare". The identical trap is live here for
 * a different reason: a `RoleResource` attachment can hand a brand-new Role a
 * system whose impact model was authored months ago, so a young Role can have a
 * perfectly real coverage figure on day one. The age explains an ABSENCE and
 * never contradicts a MEASUREMENT.
 *
 * ⚠️ THE ORDER OF EVALUATION IS PART OF THE CONTRACT, because more than one arm
 * can be true at once. `not-modelled` is tested FIRST — a Role with no coverage
 * today has nothing to compare whatever its history looks like — then the
 * prior-point arms, and the two absence arms last. `roll-up-role-coverage-
 * momentum.spec.ts` pins every overlap it can construct.
 */
export const ROLE_COVERAGE_MOMENTUM_STATES = [
  "comparable",
  "not-modelled",
  "prior-not-modelled",
  "too-few-days",
  "role-too-new",
  "not-yet-captured"
] as const;

export type RoleCoverageMomentumState = (typeof ROLE_COVERAGE_MOMENTUM_STATES)[number];

/**
 * One Role's coverage movement — today's figure against the oldest snapshot in
 * the window.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE CURRENT POINT IS LIVE. ONLY THE PRIOR POINT COMES FROM THE TABLE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The register row this rides on already carries `coverage`, computed on this
 * same request from the live model. Reading today's SNAPSHOT for the current
 * endpoint instead would put two sources under one figure and let them
 * disagree — a Role whose model changed this morning would render a movement
 * against last night's capture of itself, and the delta would be real,
 * measured, and about nothing anybody did.
 *
 * So the snapshot table supplies exactly one operand: the oldest capture at or
 * after the window opened. Today's row is written for TOMORROW's read, never
 * for this one.
 *
 * 🚨 `priorDate` AND `spanDays` ARE SERVED SO THE CAPTION CAN BE TRUE. The
 * band is drawn as a {@link ROLE_COVERAGE_MOMENTUM_WINDOW_DAYS}-day movement,
 * but the series rarely reaches exactly that far back — the oldest point is
 * wherever the capture happens to start, which for a new Role or a new
 * installation is days rather than weeks. A client that draws
 * `changePoints` under a fixed "30 days" caption without reading `spanDays` is
 * publishing a period the server did not measure.
 */
export const RoleCoverageMomentumSchema = z.strictObject({
  state: z.enum(ROLE_COVERAGE_MOMENTUM_STATES),
  /**
   * Today's coverage as a FRACTION — `0.1828`, never `18.28`. The same value
   * the row's own `coverage` union carries when it is `modelled`.
   *
   * `null` exactly when `state === "not-modelled"`. It is served flat beside
   * the delta so the two cannot be assembled from two places by a client —
   * the same rule `RoleRunMomentum.currentRuns` keeps against the `runs`
   * counters it duplicates.
   */
  currentRatio: z.number().nullable(),
  /**
   * The oldest snapshot's coverage in the window, as a FRACTION.
   *
   * `null` when no prior point was found (`role-too-new`, `not-yet-captured`)
   * AND when the point that was found carried no ratio
   * (`prior-not-modelled`). Those two cases differ in `priorDate`: a point that
   * exists but is unmodelled still has a DATE, and a point that does not exist
   * has neither. Read `state` rather than inferring from this field.
   */
  priorRatio: z.number().nullable(),
  /**
   * The UTC calendar day the prior point was measured FOR, as `YYYY-MM-DD`.
   *
   * `null` exactly when no prior snapshot was found at all — the two absence
   * states. Non-null in every state where a row was read, `prior-not-modelled`
   * included, because a reader asking "when did this Role stop being
   * measurable" is asking for exactly this date.
   *
   * A calendar day and not an instant, deliberately: the snapshot's own key is
   * one row per Role per DAY, and serving a timestamp would invite a client to
   * render a precision the measurement does not have.
   */
  priorDate: z.string().nullable(),
  /**
   * Whole days between `priorDate` and today — the span this delta ACTUALLY
   * covers, which is at most {@link ROLE_COVERAGE_MOMENTUM_WINDOW_DAYS} and is
   * usually less.
   *
   * `null` exactly when `priorDate` is `null`, in both directions.
   */
  spanDays: z.number().int().min(0).nullable(),
  /**
   * The movement in PERCENTAGE POINTS — `6` means coverage rose by six points
   * (for example 12% → 18%), `-4` means it fell by four.
   *
   * 🚨 POINTS, NOT PERCENT, AND NOT A RATIO. It is `(current − prior) × 100`,
   * a SUBTRACTION of two fractions, so it is bounded by ±100 and can never be
   * the fabricated `+100%` a percentage CHANGE produces from a zero prior.
   * It shares no formatter with the register's `coverage` (a 0–1 ratio) and
   * none with `RoleRunMomentum.changePercent` (an unbounded percentage change);
   * conflating any two of the three renders a wrong number that validates.
   *
   * 🚨 NON-NULL EXACTLY WHEN `state === "comparable"`, in both directions. The
   * biconditional is asserted by the fold's own spec, so a client may branch on
   * either one and can never meet a state carrying a number it must not draw,
   * nor a `comparable` row with nothing to draw.
   */
  changePoints: z.number().nullable()
});

export type RoleCoverageMomentum = z.infer<typeof RoleCoverageMomentumSchema>;
